import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfigService } from '../../../../config/app-config.service';
import { AppEndpoints } from '../../../../core/constants/app_endpoints';
import { ApiResponse } from '../../../../core/models/api-response';
import { AuthService } from '../../../../core/services/auth/auth.service';
import {
  PaginatedEmployeeInboxResponse,
  PaginatedSupportResponse,
  SupportConversationDetail,
} from '../models/support-conversation.model';

@Injectable({
  providedIn: 'root',
})
export class SupportChatService {
  private readonly gatewayBaseUrl: string;

  constructor(
    private appConfigService: AppConfigService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.gatewayBaseUrl = this.appConfigService.getApiGatewayBaseUrl();
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`,
    });
  }

  getCompanyConversations(
    companyId: number,
    pageNumber: number,
    pageSize: number,
    status?: number | null,
    search?: string | null
  ): Observable<PaginatedSupportResponse> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.list({
      companyId,
      pageNumber,
      pageSize,
      status: status ?? '',
      search: search ?? '',
    })}`;
    return this.http.get<PaginatedSupportResponse>(url, { headers: this.authHeaders() });
  }

  getCompanyEmployeeInbox(
    companyId: number,
    pageNumber: number,
    pageSize: number,
    status?: number | null,
    search?: string | null
  ): Observable<PaginatedEmployeeInboxResponse> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.employeeInbox({
      companyId,
      pageNumber,
      pageSize,
      status: status ?? '',
      search: search ?? '',
    })}`;
    return this.http.get<PaginatedEmployeeInboxResponse>(url, { headers: this.authHeaders() });
  }

  getEmployeeConversations(
    companyId: number,
    employeeId: string,
    pageNumber: number,
    pageSize: number,
    status?: number | null,
    search?: string | null
  ): Observable<PaginatedSupportResponse> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.employeeConversations(employeeId, {
      companyId,
      pageNumber,
      pageSize,
      status: status ?? '',
      search: search ?? '',
    })}`;
    return this.http.get<PaginatedSupportResponse>(url, { headers: this.authHeaders() });
  }

  getConversationMessages(
    conversationId: number,
    companyId: number
  ): Observable<ApiResponse<SupportConversationDetail>> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.messages(conversationId, companyId)}`;
    return this.http.get<ApiResponse<SupportConversationDetail>>(url, { headers: this.authHeaders() });
  }

  sendReply(
    conversationId: number,
    companyId: number,
    messageText: string
  ): Observable<ApiResponse<number>> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.sendMessage(conversationId, companyId)}`;
    const body = { messageText, senderType: 'Admin' };
    return this.http.post<ApiResponse<number>>(url, body, { headers: this.authHeaders() });
  }

  updateStatus(
    conversationId: number,
    companyId: number,
    status: number
  ): Observable<ApiResponse<boolean>> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.updateStatus(conversationId, companyId)}`;
    return this.http.patch<ApiResponse<boolean>>(url, { status }, { headers: this.authHeaders() });
  }

  markSeen(
    conversationId: number,
    companyId: number
  ): Observable<ApiResponse<boolean>> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.markSeen(conversationId, companyId)}`;
    return this.http.post<ApiResponse<boolean>>(url, null, { headers: this.authHeaders() });
  }

  downloadAttachment(messageId: number, companyId: number): Observable<Blob> {
    const url = `${this.gatewayBaseUrl}${AppEndpoints.supportConversations.attachment(messageId, companyId)}`;
    return this.http.get(url, {
      headers: this.authHeaders(),
      responseType: 'blob',
    });
  }
}
